/* eslint-disable camelcase */
import React, { Component } from "react";
import PropTypes from "prop-types";
// import utils from '@utils';
import HubspotService from "@services/HubspotService";
import { OrganizationService, OfficerService } from "@services";
import utils from "@utils";
import { Modal } from "../../../../components";

class OfficerSyncModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			results: [],
			killSync: false,
			progressCount: 0,
			staffMembers: [],
			loading: true,
		};
	}

	/**
	 * Retrieves a paginated list of officers per their jurisdiction
	 * @param {Array} prevOfficers A list of the previous existing officers
	 * @param {Number} pageNumber Used to paginate the list
	 */
	getJurisdictionOfficers = (prevOfficers = [], pageNumber = 1) => {
		const { jurisdictionId } = this.props;
		let officers = [...prevOfficers];
		return OfficerService.search(
			{ pageNumber, jurisdictionId, displayCount: 100 },
			{ FirstName: "", LastName: "" },
		).then(({ data: { ItemList } }) => {
			officers = [...officers, ...ItemList].map(i => ({ ...i, IsOfficer: true }));
			if (ItemList.length >= 100)
				return this.getJurisdictionOfficers(officers, pageNumber + 1);
			return officers;
		});
	};

	/**
	 * Retrieves a paginated list of users per their organization
	 * This list will be used for syncing staff members to hubspot
	 * @param {Array} prevUsers A list of the previous existing users
	 * @param {Number} pageNumber Used to paginate the list
	 */
	getAllOrganizationUsers = (prevUsers = [], pageNumber = 1) => {
		const { organizationId } = this.props;
		const {
			details: { PrimaryContact, BillingContact },
		} = this.props;
		let allUsers = [...prevUsers];
		return OrganizationService.getContacts(organizationId, {
			pageNumber,
			displayCount: 100,
		}).then(({ data: { ItemList } }) => {
			// update the Email key to EmailAddress for hubspot syncing purposes
			allUsers = [...allUsers, ...ItemList].map(user => ({
				...user,
				IsPrimaryContact:
					user.EmailAddress?.toLowerCase() ===
					PrimaryContact?.EmailAddress?.toLowerCase(),
				IsBillingContact:
					user.EmailAddress?.toLowerCase() ===
					BillingContact?.EmailAddress?.toLowerCase(),
			}));

			if (ItemList.length >= 100)
				return this.getAllOrganizationUsers(allUsers, pageNumber + 1);
			return allUsers;
		});
	};

	fetchMembers = async () => {
		const { syncType } = this.props;
		switch (syncType) {
			case "officers":
				return this.getJurisdictionOfficers();
			case "staff":
				return this.getAllOrganizationUsers();
			default:
				return [];
		}
	};

	syncMembers = async () => {
		// Iterate through the list of officers and one by one create/resolve a promise for handling progressive calls to the Hubspot API
		const { lang } = global;
		const { staffMembers } = this.state;

		if (staffMembers.length !== 0) {
			this.setState({ killSync: false }, () =>
				staffMembers
					.map(({ EmailAddress }) => () => {
						const { killSync } = this.state;
						// Stops all search operations
						if (killSync) return Promise.reject();
						return HubspotService.searchContactsByEmail(
							EmailAddress.replace(/\*/g, ""),
						);
					})
					.reduce(async (prev, accumulator, currentIndex) => {
						try {
							const officerData = await prev.then(accumulator);
							await this.fetchAndSyncStaff(officerData.data, currentIndex);
						} catch (error) {
							// If an error occurs notify that the user cannot be synced and proceed to the next request
							const staff = staffMembers[currentIndex];
							const { FirstName, LastName, EmailAddress, Id: OfficerId } = staff;
							const syncedStaff = {
								OfficerId,
								FirstName,
								LastName,
								EmailAddress,
								syncError: {
									message:
										error?.response?.status === 429
											? lang.t("Request limit reached")
											: lang.t("User could not be synced"),
								},
								synced: false,
							};
							this.setState(prevState => ({
								progressCount: prevState.progressCount + 1,
								results: [...prevState.results, syncedStaff],
							}));
						}
						return prev;
					}, Promise.resolve()),
			);
		}
	};

	/** Invokes any methods required for when opening the Officer Sync Modal */
	onModalOpen = () => {
		this.fetchMembers()
			.then(members => {
				this.setState({ staffMembers: members, loading: false }, () => {
					this.syncMembers();
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Unable to get users. Please try again.",
					title: "Error",
					timeout: 1500,
				});
			});
	};

	/**
	 * Performs an syncing of associated officers between a Hubspot company and an RK organization
	 * @param {Object} officerData The officer that returns from Hubspot
	 * @param {Number} currentIndex The index used for locating the current office contact
	 */
	fetchAndSyncStaff = async (officerData, currentIndex) => {
		const {
			hubspot: { company },
		} = this.props;
		const { staffMembers } = this.state;
		const { killSync } = this.state;
		// Stops all syncing operations
		if (killSync) return Promise.reject();

		let response;
		let isError = false;
		let syncedStaff;
		let staffRole;
		const result = officerData.results[0];
		const staff = staffMembers[currentIndex];
		const {
			FirstName,
			LastName,
			EmailAddress,
			Id: OfficerId,
			IsAdmin,
			IsCoordinator,
			IsOfficer,
			IsPrimaryContact,
			IsBillingContact,
		} = staff;

		// Explicitly check the user role of each staff member since this check can be conducted on two separate lists
		if (IsOfficer) staffRole = "Officer - App User";
		else if (IsPrimaryContact) staffRole = "Officer - Primary POC";
		else if (IsBillingContact) staffRole = "Officer - Billing POC";
		else if (IsAdmin === false && IsCoordinator === false) staffRole = "Officer – LEA Staff";
		else if (IsAdmin === true && IsCoordinator === false) staffRole = "Officer – LEA Admin";
		else if (IsAdmin === false && IsCoordinator === true)
			staffRole = "Officer – LEA Coordinator";

		try {
			if (result) {
				// If the officer is already a contact update their record with the latest information
				response = await HubspotService.updateContactProperties(result.id, {
					firstname: FirstName,
					lastname: LastName,
					email: EmailAddress,
					contact_type: staffRole,
					officer_id: OfficerId,
				});
			} else {
				// Otherwise, attempt to create a new record for that officer
				response = await HubspotService.createContact(
					FirstName,
					LastName,
					EmailAddress,
					staffRole,
					OfficerId,
				);
			}
		} catch (error) {
			isError = true;
			syncedStaff = {
				OfficerId,
				FirstName,
				LastName,
				EmailAddress,
				syncError: error.response.data,
				synced: false,
			};
		}
		if (!isError) {
			syncedStaff = {
				OfficerId: response.data.properties.officer_id,
				Id: response.data.id,
				FirstName: response.data.properties.firstname,
				LastName: response.data.properties.lastname,
				EmailAddress: response.data.properties.email,
				synced: false,
			};
		}

		// If another contact with the same email address exists, use the existing contact ID to associate it with the current agency.
		// For newly created contacts, use the contact ID provided
		const contactId =
			syncedStaff.syncError?.category === "CONFLICT"
				? syncedStaff.syncError.message.replace(/[^0-9]/g, "")
				: syncedStaff.Id;
		// If a contact ID to associate with the agency has been established, make the request to do so
		if (contactId) {
			try {
				await HubspotService.syncCompanyToContact(company.id, contactId);
				syncedStaff.Id = contactId;
				syncedStaff.synced = true;
			} catch (error) {
				syncedStaff.syncError = error.response.data;
			}
		}

		// Update the progress counter and results array and continue to the next officer
		this.setState(prevState => ({
			progressCount: prevState.progressCount + 1,
			results: [...prevState.results, syncedStaff],
		}));

		return Promise.resolve();
	};

	hideModal = () => {
		const { results } = this.state;
		const { onHide } = this.props;
		this.setState({ killSync: true });
		onHide(results);
	};

	resetModal = () =>
		this.setState({
			results: [],
			progressCount: 0,
			killSync: true,
			staffMembers: [],
			loading: true,
		});

	render() {
		const { lang } = global;
		const { show } = this.props;
		const { loading, progressCount, results, staffMembers } = this.state;

		return (
			<Modal
				id="hs-offsync"
				show={show}
				onEntered={() => this.onModalOpen()}
				onHide={() => this.hideModal()}
				onExited={this.resetModal}
			>
				<Modal.Header closeButton>
					<Modal.Title>{lang.t("Officer Hubspot Sync")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<If condition={loading}>
						<div className="text-center">
							<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
						</div>
					</If>
					<If condition={!loading}>
						{staffMembers.length > 0 ? (
							<>
								<div className="progress">
									<div
										className="progress-bar"
										role="progressbar"
										aria-valuenow={Math.ceil(
											(progressCount / staffMembers.length || 0) * 100,
										)}
										aria-valuemin="0"
										aria-valuemax="100"
										style={{
											width: `${Math.ceil((progressCount / staffMembers.length || 0) * 100)}%`,
										}}
									>
										{Math.ceil(
											(progressCount / staffMembers.length || 0) * 100,
										)}
										%
									</div>
								</div>
								{staffMembers.map(staff => {
									const hsStaff =
										results.find(
											({ EmailAddress }) =>
												EmailAddress.toLowerCase() ===
												staff.EmailAddress.toLowerCase(),
										) || {};
									return (
										<div key={`synced-staff_${staff.Id}`}>
											<span className="text-capitalize">
												{staff.FirstName} {staff.LastName}
											</span>
											<Choose>
												<When condition={hsStaff.synced}>
													&nbsp;
													<i className="glyphicon glyphicon-ok-circle text-success" />
												</When>
												<When condition={hsStaff.syncError}>
													&nbsp;
													<i className="glyphicon glyphicon-remove-circle text-danger" />
													<br />
													<em>{hsStaff.syncError?.message}</em>
												</When>
											</Choose>
										</div>
									);
								})}
							</>
						) : (
							lang.t("No officers to sync")
						)}
					</If>
				</Modal.Body>
			</Modal>
		);
	}
}

OfficerSyncModal.propTypes = {
	show: PropTypes.bool,
	hubspot: PropTypes.shape({
		contacts: PropTypes.array,
		company: PropTypes.object,
	}),
	syncType: PropTypes.string,
	onHide: PropTypes.func,
	jurisdictionId: PropTypes.number,
	organizationId: PropTypes.string,
	details: PropTypes.object,
};

OfficerSyncModal.defaultProps = {
	show: false,
	hubspot: { contacts: [], company: undefined },
	syncType: "",
	jurisdictionId: 0,
	organizationId: "",
	onHide: () => {},
	details: {},
};

export default OfficerSyncModal;
